import {useEffect, useRef, useState, type KeyboardEvent} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {MicOff, Pause, PhoneIncoming, PhoneMissed, PhoneOutgoing} from 'lucide-react';
import {useFetchContactHistoryQuery, type Call} from '../../../store/features/calls/callsService';
import {CallAvatar} from '../components/CallAvatar';
import {OutputPicker} from '../components/OutputPicker';
import {MinimizeButton, StageControl, StageEndButton, StatusPill} from '../components/controls';
import {useElapsed} from '../hooks/useElapsed';
import type {ControlSpec} from '../group/useGroupControls';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import {matrixSurfaceMessages as dm} from '../matrix/matrixMessages';
import type {PhoneCallLeg} from '../model/types';
import {formatCallDuration} from '../services/format';
import {LineChip} from './PhoneChips';
import {lineTextOf, phoneTitleOf} from './phoneText';
import type {PhoneActions} from './usePhoneActions';
import type {PhoneControls} from './usePhoneControls';
import {phoneMessages as m} from './phoneMessages';

const HISTORY_LIMIT = 20;

type AsideTab = 'details' | 'history';

const DockControl = ({spec}: {spec: ControlSpec}) => (
    <StageControl
        icon={spec.icon}
        label={spec.label}
        caption={spec.caption}
        tone={spec.tone}
        pressed={spec.pressed}
        expanded={spec.expanded}
        hasPopup={spec.hasPopup}
        disabled={spec.disabled}
        testId={spec.testId}
        onClick={spec.onClick}
    />
);

const DetailRow = ({label, value}: {label: string; value: string}) => (
    <div className="flex justify-between gap-3 border-b border-[var(--phq-call-line)] py-2.5 text-sm">
        <span className="text-[var(--phq-call-ink-muted)]">{label}</span>
        <span className="min-w-0 truncate text-right font-semibold tabular-nums">{value}</span>
    </div>
);

const isMissed = (call: Call): boolean => call.type === 'INBOUND' && !call.bridgetAt && !call.forwardedTo;

const HistoryRow = ({call}: {call: Call}) => {
    const intl = useIntl();
    const missed = isMissed(call);
    const Icon = missed ? PhoneMissed : call.type === 'INBOUND' ? PhoneIncoming : PhoneOutgoing;
    const label = intl.formatMessage(missed ? m.historyMissed : call.type === 'INBOUND' ? m.historyIncoming : m.historyOutgoing);
    const started = call.startedAt ? new Date(call.startedAt) : undefined;
    const duration = call.bridgetAt && call.finishedAt ? Date.parse(call.finishedAt) - Date.parse(call.bridgetAt) : undefined;
    return (
        <li className="flex items-center gap-3 border-b border-[var(--phq-call-line)] py-2.5 text-sm">
            <Icon size={16} className={missed ? 'text-[var(--phq-call-danger-soft)]' : 'text-[var(--phq-call-ink-muted)]'} aria-hidden="true"/>
            <span className={`flex-1 font-semibold ${missed ? 'text-[var(--phq-call-danger-soft)]' : ''}`}>{label}</span>
            {duration !== undefined && duration > 0 && <span className="tabular-nums text-[var(--phq-call-ink-muted)]">{formatCallDuration(duration)}</span>}
            {started && (
                <span className="tabular-nums text-[var(--phq-call-ink-muted)]">
                    {intl.formatDate(started, {day: 'numeric', month: 'short'})} {intl.formatTime(started, {hour: '2-digit', minute: '2-digit'})}
                </span>
            )}
        </li>
    );
};

const HistoryList = ({contactId}: {contactId?: number}) => {
    const intl = useIntl();
    const {data} = useFetchContactHistoryQuery({contactId: contactId ?? 0, limit: HISTORY_LIMIT}, {skip: contactId === undefined});
    const calls = Object.values(data?.groupedCalls ?? {}).flat();
    if (calls.length === 0) {
        return <p className="py-3 text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.historyEmpty)}</p>;
    }
    return <ul data-testid="phone-call-history">{calls.map(call => <HistoryRow key={call.callId} call={call}/>)}</ul>;
};

const DetailsAside = ({leg, recording}: {leg: PhoneCallLeg; recording?: boolean}) => {
    const intl = useIntl();
    const [tab, setTab] = useState<AsideTab>('details');
    const number = leg.identity.number ?? leg.dialNumber;
    const tabs: {id: AsideTab; label: string}[] = [
        {id: 'details', label: intl.formatMessage(m.detailsTab)},
        {id: 'history', label: intl.formatMessage(m.historyTab)},
    ];
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            setTab(current => (current === 'details' ? 'history' : 'details'));
        }
    };
    return (
        <aside aria-label={intl.formatMessage(m.detailsTitle)} className="flex w-[340px] shrink-0 flex-col border-l border-[var(--phq-call-line)] bg-[var(--phq-call-surface)]" data-testid="phone-call-details">
            <div className="flex h-14 items-center px-4">
                <div role="tablist" aria-label={intl.formatMessage(m.detailsTitle)} className="flex rounded-[10px] bg-[var(--phq-call-surface-muted)] p-[3px]" onKeyDown={onKeyDown}>
                    {tabs.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.id}
                            tabIndex={tab === item.id ? 0 : -1}
                            onClick={() => setTab(item.id)}
                            className={`h-[30px] flex-1 rounded-lg px-4 text-[13px] ${tab === item.id
                                ? 'bg-[var(--phq-call-segment-selected)] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.5)]'
                                : 'font-medium text-[var(--phq-call-ink-muted)]'}`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
            <div role="tabpanel" className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4">
                {tab === 'details' ? (
                    <>
                        {leg.line && <DetailRow label={intl.formatMessage(m.rowLine)} value={lineTextOf(leg.line)}/>}
                        {number && <DetailRow label={intl.formatMessage(m.rowNumber)} value={number}/>}
                        {leg.connectedAt && <DetailRow label={intl.formatMessage(m.rowStarted)} value={intl.formatTime(leg.connectedAt, {hour: '2-digit', minute: '2-digit'})}/>}
                        {recording !== undefined && <DetailRow label={intl.formatMessage(m.rowRecording)} value={intl.formatMessage(recording ? m.on : m.off)}/>}
                    </>
                ) : (
                    <HistoryList contactId={leg.identity.contactId}/>
                )}
            </div>
        </aside>
    );
};

interface PhoneStageProps {
    readonly leg: PhoneCallLeg;
    readonly actions: PhoneActions;
    readonly controls: PhoneControls;
    readonly recording?: boolean;
    readonly outputOpen: boolean;
    readonly onCloseOutput: () => void;
    readonly onMinimize: () => void;
}

export const PhoneStage = ({leg, actions, controls, recording, outputOpen, onCloseOutput, onMinimize}: PhoneStageProps) => {
    const intl = useIntl();
    const timer = useElapsed(leg.connectedAt);
    const region = useRef<HTMLElement>(null);
    const dock = useRef<HTMLDivElement>(null);
    const title = phoneTitleOf(leg);

    useEffect(() => {
        region.current?.focus();
    }, []);

    useEffect(() => {
        if (!outputOpen) {
            return;
        }
        const onPointerDown = (event: PointerEvent) => {
            if (!dock.current?.contains(event.target as Node)) {
                onCloseOutput();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [onCloseOutput, outputOpen]);

    const subline = leg.phase === 'calling'
        ? intl.formatMessage(m.calling)
        : leg.held && timer
            ? intl.formatMessage(m.onHold, {timer})
            : timer ?? intl.formatMessage(m.connecting);

    const onDockKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && outputOpen) {
            event.stopPropagation();
            onCloseOutput();
        }
    };

    return createPortal(
        <section
            ref={region}
            tabIndex={-1}
            aria-label={intl.formatMessage(recording ? m.callRegionRecording : m.callRegion, {name: title})}
            data-testid="phone-call-stage"
            className="phq-call-ui phq-call-enter fixed bottom-0 left-14 right-0 top-0 z-[55] flex bg-[var(--phq-call-canvas)] outline-none"
        >
            <div className="relative flex min-w-0 flex-1 flex-col items-center justify-end gap-6 [background-image:var(--phq-call-stage-gradient)] pb-10">
                <div className="absolute left-4 top-4 flex gap-2">
                    {leg.line && <LineChip line={leg.line} size="stage"/>}
                    {leg.muted && !leg.held && <StatusPill tone="muted" icon={<MicOff size={16} className="text-[var(--phq-call-danger-ink)]" aria-hidden="true"/>}>{intl.formatMessage(gs.noteMuted)}</StatusPill>}
                    {leg.held && <StatusPill tone="reconnecting" icon={<Pause size={16} aria-hidden="true"/>}>{intl.formatMessage(dm.captionHold)}</StatusPill>}
                </div>
                <div className="absolute right-4 top-3">
                    <MinimizeButton label={intl.formatMessage(gs.minimize)} testId="phone-call-minimize" onClick={onMinimize}/>
                </div>
                <div className="flex flex-1 flex-col items-center justify-center gap-3 pb-[72px] text-center">
                    <CallAvatar identity={leg.identity} size={128}/>
                    <h1 className="mt-3 text-[30px] font-semibold leading-[1.2] tabular-nums">{title}</h1>
                    <p className="text-lg font-medium tabular-nums text-[var(--phq-call-ink-muted)]" data-testid="call-timer">{subline}</p>
                </div>
                <div ref={dock} className="relative flex gap-4" onKeyDown={onDockKeyDown}>
                    {[controls.microphone, controls.keypad, controls.speaker, controls.hold].map(spec => <DockControl key={spec.id} spec={spec}/>)}
                    <DockControl spec={controls.devices}/>
                    {outputOpen && (
                        <div className="absolute bottom-[calc(100%+8px)] left-[82px] w-[300px] rounded-2xl border border-[var(--phq-call-line)] bg-[var(--phq-call-canvas)] p-2 phq-call-shadow-menu">
                            <div className="px-3 pb-1 pt-2 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(dm.outputTitle)}</div>
                            <OutputPicker current={actions.speaker} onSelect={deviceId => {
                                actions.setOutput(deviceId);
                                onCloseOutput();
                            }}/>
                        </div>
                    )}
                </div>
                <StageEndButton label={intl.formatMessage(leg.phase === 'calling' ? m.cancel : m.endCall)} testId="hangup" onClick={actions.end}/>
            </div>
            <DetailsAside leg={leg} recording={recording}/>
        </section>,
        document.body,
    );
};
